import { useEffect, useMemo, useState } from 'react';
import {
  useGenerateRoster, useRosterBalances, useSavePreference, useSaveTargets,
  useShiftPreferences, useStaffingTargets, useUpdateEmployee,
} from '../api/hooks';
import type { Employee, RosterPlan, ShiftLocation, StaffingTarget } from '../api/types';
import { useLang } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { Icon } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';

const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const targetKey = (loc: string, weekday: number) => `${loc}|${weekday}`;

/**
 * The planning desk: how many people each sede needs, what everyone prefers,
 * and a generator that proposes a roster from the two. Nothing is written until
 * the proposal has been looked at.
 */
export function RosterPlanner({ locations, employees, from, to }: {
  locations: ShiftLocation[]; employees: Employee[]; from: string; to: string;
}) {
  const { t } = useLang();
  const confirm = useConfirm();
  const targetsQ = useStaffingTargets();
  const prefsQ = useShiftPreferences();
  const balancesQ = useRosterBalances(from, to);
  const saveTargets = useSaveTargets();
  const savePref = useSavePreference();
  const generate = useGenerateRoster();
  const updateEmployee = useUpdateEmployee();
  const saveHours = (body: { id: string; weeklyHours: number | null }) =>
    updateEmployee.mutate(body, { onError: () => toast.error(t('Salvataggio delle ore non riuscito.')) });

  const [tab, setTab] = useState<'need' | 'pref' | 'balance'>('need');
  const [plan, setPlan] = useState<RosterPlan | null>(null);
  const [times, setTimes] = useState({ start: '09:00', end: '17:00' });

  // Sedi arranged case by case (HOME) never take part in the automatic plan, so
  // they are not worth a staffing row either.
  const planned = locations.filter((l) => l.autoAssign);
  const manual = locations.filter((l) => !l.autoAssign);

  // The needs table is edited locally and saved in one go — it is a grid, not a form.
  const [draft, setDraft] = useState<Record<string, number>>({});
  useEffect(() => {
    const next: Record<string, number> = {};
    for (const tg of targetsQ.data ?? []) next[targetKey(tg.locationKey, tg.weekday)] = tg.count;
    setDraft(next);
  }, [targetsQ.data]);

  const dirty = useMemo(() => {
    const saved: Record<string, number> = {};
    for (const tg of targetsQ.data ?? []) saved[targetKey(tg.locationKey, tg.weekday)] = tg.count;
    const keys = new Set([...Object.keys(saved), ...Object.keys(draft)]);
    return [...keys].some((k) => (saved[k] ?? 0) !== (draft[k] ?? 0));
  }, [draft, targetsQ.data]);

  const totalWanted = Object.values(draft).reduce((n, v) => n + (v || 0), 0);

  const onSaveTargets = async () => {
    const targets: StaffingTarget[] = [];
    for (const l of planned) {
      for (let weekday = 0; weekday < 7; weekday++) {
        const count = draft[targetKey(l.key, weekday)] ?? 0;
        if (count > 0) targets.push({ locationKey: l.key, weekday, count });
      }
    }
    try {
      await saveTargets.mutateAsync(targets);
      toast.success(t('Fabbisogno salvato.'));
    } catch {
      toast.error(t('Salvataggio del fabbisogno non riuscito.'));
    }
  };

  const onGenerate = async (apply: boolean) => {
    if (apply) {
      const ok = await confirm({
        title: t('Applicare la proposta?'),
        message: t('I {n} turni proposti verranno scritti nella griglia. I turni già presenti non vengono toccati.', { n: plan?.assignments.length ?? 0 }),
        confirmLabel: t('Applica'),
      });
      if (!ok) return;
    }
    try {
      const result = await generate.mutateAsync({ from, to, startTime: times.start, endTime: times.end, apply });
      setPlan(result);
      if (apply) toast.success(t('{n} turni inseriti.', { n: result.assignments.length }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Generazione non riuscita.'));
    }
  };

  return (
    <div className="rp">
      <div className="role-switch rp__tabs">
        <button className={tab === 'need' ? 'is-active' : ''} onClick={() => setTab('need')}>{t('Fabbisogno')}</button>
        <button className={tab === 'pref' ? 'is-active' : ''} onClick={() => setTab('pref')}>{t('Preferenze')}</button>
        <button className={tab === 'balance' ? 'is-active' : ''} onClick={() => setTab('balance')}>{t('Ore e ferie')}</button>
      </div>

      {tab === 'need' && (
        <section className="card rp__card">
          <header className="rp__head">
            <div>
              <b>{t('Quante persone servono')}</b>
              <p className="muted rp__hint">{t('Per ogni sede e giorno della settimana. 0 = sede chiusa o non presidiata.')}</p>
            </div>
            <button className="btn btn--brand btn--sm" disabled={!dirty || saveTargets.isPending} onClick={onSaveTargets}>
              {saveTargets.isPending ? t('Salvataggio…') : t('Salva fabbisogno')}
            </button>
          </header>
          {planned.length === 0 ? (
            <p className="muted">{t('Nessuna sede configurata per la pianificazione automatica.')}</p>
          ) : (
            <div className="rp__scroll">
              <table className="rp__table">
                <thead>
                  <tr>
                    <th scope="col" className="rp__sticky-col">{t('Sede')}</th>
                    {DOW.map((d, i) => <th scope="col" key={d} className={i >= 5 ? 'is-weekend' : ''}>{t(d)}</th>)}
                    <th scope="col">{t('Tot')}</th>
                  </tr>
                </thead>
                <tbody>
                  {planned.map((l) => {
                    const rowTotal = DOW.reduce((n, _, i) => n + (draft[targetKey(l.key, i)] ?? 0), 0);
                    return (
                      <tr key={l.key}>
                        <th scope="row" className="rp__sticky-col rp__loc">{l.short ?? l.key.toUpperCase()}<span className="muted">{l.label}</span></th>
                        {DOW.map((_, weekday) => (
                          <td key={weekday} className={weekday >= 5 ? 'is-weekend' : ''}>
                            <input
                              className="input rp__num" type="number" min={0} max={50} inputMode="numeric"
                              aria-label={`${l.label} · ${DOW[weekday]}`}
                              value={draft[targetKey(l.key, weekday)] ?? 0}
                              onChange={(e) => setDraft((d) => ({ ...d, [targetKey(l.key, weekday)]: Math.max(0, Number(e.target.value) || 0) }))}
                            />
                          </td>
                        ))}
                        <td className="rp__rowtot">{rowTotal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted rp__hint">
            {t('Totale presenze richieste a settimana:')} <b>{totalWanted}</b>
            {manual.length > 0 && <> · {t('Fuori dal piano automatico:')} {manual.map((l) => l.short ?? l.key.toUpperCase()).join(', ')}</>}
          </p>
        </section>
      )}

      {tab === 'pref' && (
        <PreferenceList
          employees={employees}
          locations={locations}
          prefs={prefsQ.data ?? []}
          onSave={async (employeeId, body) => {
            try {
              await savePref.mutateAsync({ employeeId, ...body });
              toast.success(t('Preferenze salvate.'));
            } catch {
              toast.error(t('Salvataggio delle preferenze non riuscito.'));
            }
          }}
        />
      )}

      {tab === 'balance' && (
        <section className="card rp__card">
          <header className="rp__head">
            <div>
              <b>{t('Ore pianificate e assenze usate')}</b>
              <p className="muted rp__hint">{t('Le ore massime settimanali sono modificabili qui: il generatore non le supera mai e la griglia avvisa quando vengono sforate. Vuoto = 40h.')}</p>
            </div>
          </header>
          <div className="rp__scroll">
            <table className="rp__table rp__table--read">
              <thead>
                <tr>
                  <th scope="col" className="rp__sticky-col">{t('Persona')}</th>
                  <th scope="col">{t('Max ore/sett.')}</th>
                  <th scope="col">{t('Ore periodo')}</th>
                  <th scope="col">{t('Ferie usate')}</th>
                  <th scope="col">{t('Ferie residue')}</th>
                  <th scope="col">{t('Permessi')}</th>
                  <th scope="col">{t('Malattia')}</th>
                </tr>
              </thead>
              <tbody>
                {(balancesQ.data ?? []).map((b) => {
                  const over = b.byWeek.some((w) => w.over);
                  const contract = employees.find((e) => e.id === b.employeeId)?.weeklyHours ?? null;
                  return (
                    <tr key={b.employeeId} className={over ? 'is-over' : ''}>
                      <th scope="row" className="rp__sticky-col">{b.name}</th>
                      <td>
                        <input
                          className="input rp__num" type="number" min={0} max={80} placeholder="40"
                          aria-label={t('Ore massime settimanali di {name}', { name: b.name })}
                          defaultValue={contract ?? ''}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            const next = raw === '' ? null : Math.max(0, Math.min(80, Number(raw) || 0));
                            if (next !== contract) saveHours({ id: b.employeeId, weeklyHours: next });
                          }}
                        />
                      </td>
                      <td>{b.plannedHours}h {over && <span className="rp__flag" title={t('Almeno una settimana supera il contratto')}>!</span>}</td>
                      <td>{b.leave.ferieDays} {t('gg')}</td>
                      <td className={b.leave.remainingDays < 0 ? 'is-negative' : ''}>{b.leave.remainingDays} {t('gg')}</td>
                      <td>{b.leave.permessoHours}h</td>
                      <td>{b.leave.malattiaDays} {t('gg')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {balancesQ.data?.length === 0 && <p className="muted">{t('Nessun dipendente attivo.')}</p>}
        </section>
      )}

      <section className="card rp__card rp__gen">
        <header className="rp__head">
          <div>
            <b>{t('Genera turni')}</b>
            <p className="muted rp__hint">{t('Riempie i buchi del periodo mostrato, rispettando contratti, preferenze e assenze approvate. I turni già inseriti restano.')}</p>
          </div>
        </header>
        <div className="rp__gen-row">
          <label className="rp__field"><span className="muted">{t('Inizio')}</span>
            <input className="input" type="time" value={times.start} onChange={(e) => setTimes((s) => ({ ...s, start: e.target.value }))} />
          </label>
          <label className="rp__field"><span className="muted">{t('Fine')}</span>
            <input className="input" type="time" value={times.end} onChange={(e) => setTimes((s) => ({ ...s, end: e.target.value }))} />
          </label>
          <button className="btn btn--sm" disabled={generate.isPending} onClick={() => onGenerate(false)}>
            <Icon name="refresh" size={13} /> {generate.isPending ? t('Calcolo…') : t('Anteprima')}
          </button>
          <button className="btn btn--brand btn--sm" disabled={!plan || plan.applied || !plan.assignments.length} onClick={() => onGenerate(true)}>
            {t('Applica')}
          </button>
        </div>

        {plan && <PlanPreview plan={plan} locations={locations} />}
      </section>
    </div>
  );
}

/** The proposal, summarised the way a reviewer reads it: gaps first, then load. */
function PlanPreview({ plan, locations }: { plan: RosterPlan; locations: ShiftLocation[] }) {
  const { t } = useLang();
  const locLabel = (key: string) => locations.find((l) => l.key === key)?.short ?? key.toUpperCase();

  return (
    <div className="rp__plan">
      <div className="rp__plan-stats">
        <span className="rp__stat"><b>{plan.assignments.length}</b> {t('turni proposti')}</span>
        <span className="rp__stat"><b>{plan.offDays.length}</b> {t('giorni di riposo')}</span>
        <span className={`rp__stat${plan.missing ? ' is-warn' : ''}`}><b>{plan.missing}</b> {t('slot scoperti')}</span>
        <span className="rp__stat"><b>{plan.shiftHours}h</b> {t('per turno')}</span>
        {plan.applied && <span className="rp__stat is-ok">{t('Applicata')}</span>}
      </div>

      {/* Falling short of the requested headcount is the thing a planner must
          not miss, so it gets a banner rather than a line in a list. */}
      {plan.missing > 0 && (
        <div className="rp__alert" role="status">
          <Icon name="warn" size={15} />
          <div>
            <b>{t('Fabbisogno non raggiunto: mancano {n} presenze.', { n: plan.missing })}</b>
            <p>{t('Nessuno era disponibile per questi turni — servono ore, disponibilità o preferenze diverse.')}</p>
            <ul className="rp__gap-list">
              {plan.gaps.slice(0, 12).map((g, i) => (
                <li key={i}>
                  {new Date(`${g.date}T00:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' · '}<b>{locLabel(g.locationKey)}</b>{' '}−{g.missing}
                </li>
              ))}
              {plan.gaps.length > 12 && <li className="muted">+{plan.gaps.length - 12}…</li>}
            </ul>
          </div>
        </div>
      )}

      <div className="rp__scroll">
        <table className="rp__table rp__table--read">
          <thead>
            <tr>
              <th scope="col" className="rp__sticky-col">{t('Persona')}</th>
              <th scope="col">{t('Giorni')}</th>
              <th scope="col">{t('Riposi')}</th>
              <th scope="col">{t('Ore')}</th>
              <th scope="col">{t('Weekend/festivi')}</th>
            </tr>
          </thead>
          <tbody>
            {plan.load.map((l) => (
              <tr key={l.employeeId}>
                <th scope="row" className="rp__sticky-col">{l.name}</th>
                <td>{l.days}</td>
                <td>{l.off}</td>
                <td>{l.hours}h <span className="muted">/ {l.weeklyHours}h</span></td>
                <td>{l.unsocial}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** One editable card per person — reads well on a phone, wraps into columns on a desktop. */
function PreferenceList({ employees, locations, prefs, onSave }: {
  employees: Employee[];
  locations: ShiftLocation[];
  prefs: { employeeId: string; preferredLocs: string[]; avoidLocs: string[]; noWeekends: boolean; maxDaysPerWeek: number | null }[];
  onSave: (employeeId: string, body: { preferredLocs: string[]; avoidLocs: string[]; noWeekends: boolean; maxDaysPerWeek: number | null }) => Promise<void>;
}) {
  const { t } = useLang();
  const [open, setOpen] = useState<string | null>(null);
  const byId = useMemo(() => new Map(prefs.map((p) => [p.employeeId, p])), [prefs]);

  return (
    <section className="card rp__card">
      <header className="rp__head">
        <div>
          <b>{t('Preferenze del personale')}</b>
          <p className="muted rp__hint">{t('Le sedi preferite sono un desiderio, quelle escluse e il “niente weekend” sono vincoli che il generatore non viola mai.')}</p>
        </div>
      </header>
      <ul className="rp__people">
        {employees.map((e) => {
          const p = byId.get(e.id);
          const isOpen = open === e.id;
          return (
            <li key={e.id} className={`rp__person${isOpen ? ' is-open' : ''}`}>
              <button className="rp__person-head" onClick={() => setOpen(isOpen ? null : e.id)} aria-expanded={isOpen}>
                <span className="rp__person-name">{e.name}</span>
                <span className="rp__person-sum muted">{summary(p, locations, t)}</span>
                <Icon name={isOpen ? 'chevU' : 'chevD'} size={14} />
              </button>
              {isOpen && (
                <PreferenceForm
                  locations={locations}
                  value={p ?? { employeeId: e.id, preferredLocs: [], avoidLocs: [], noWeekends: false, maxDaysPerWeek: null }}
                  onSave={(body) => onSave(e.id, body)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function summary(
  p: { preferredLocs: string[]; avoidLocs: string[]; noWeekends: boolean; maxDaysPerWeek: number | null } | undefined,
  locations: ShiftLocation[],
  t: (s: string) => string,
) {
  if (!p) return t('nessuna preferenza');
  const label = (k: string) => locations.find((l) => l.key === k)?.short ?? k.toUpperCase();
  const bits: string[] = [];
  if (p.preferredLocs.length) bits.push(`★ ${p.preferredLocs.map(label).join(', ')}`);
  if (p.avoidLocs.length) bits.push(`✕ ${p.avoidLocs.map(label).join(', ')}`);
  if (p.noWeekends) bits.push(t('no weekend'));
  if (p.maxDaysPerWeek) bits.push(`max ${p.maxDaysPerWeek}gg`);
  return bits.join(' · ') || t('nessuna preferenza');
}

function PreferenceForm({ locations, value, onSave }: {
  locations: ShiftLocation[];
  value: { preferredLocs: string[]; avoidLocs: string[]; noWeekends: boolean; maxDaysPerWeek: number | null };
  onSave: (body: { preferredLocs: string[]; avoidLocs: string[]; noWeekends: boolean; maxDaysPerWeek: number | null }) => Promise<void>;
}) {
  const { t } = useLang();
  const [preferred, setPreferred] = useState<string[]>(value.preferredLocs);
  const [avoid, setAvoid] = useState<string[]>(value.avoidLocs);
  const [noWeekends, setNoWeekends] = useState(value.noWeekends);
  const [maxDays, setMaxDays] = useState<string>(value.maxDaysPerWeek ? String(value.maxDaysPerWeek) : '');
  const [saving, setSaving] = useState(false);

  // A sede cannot be wanted and refused at once — picking one clears the other.
  const toggle = (key: string, list: string[], set: (v: string[]) => void, other: string[], setOther: (v: string[]) => void) => {
    if (list.includes(key)) set(list.filter((k) => k !== key));
    else { set([...list, key]); setOther(other.filter((k) => k !== key)); }
  };

  const save = async () => {
    setSaving(true);
    await onSave({ preferredLocs: preferred, avoidLocs: avoid, noWeekends, maxDaysPerWeek: maxDays ? Number(maxDays) : null });
    setSaving(false);
  };

  return (
    <div className="rp__form">
      <div className="rp__form-row">
        <span className="rp__form-label">{t('Sedi preferite')}</span>
        <div className="rp__chips">
          {locations.map((l) => (
            <button key={l.key} type="button" className={`rp__chip${preferred.includes(l.key) ? ' is-on' : ''}`}
                    onClick={() => toggle(l.key, preferred, setPreferred, avoid, setAvoid)}>
              {preferred.indexOf(l.key) >= 0 && <b>{preferred.indexOf(l.key) + 1}.</b>} {l.short ?? l.key.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="rp__form-row">
        <span className="rp__form-label">{t('Da escludere')}</span>
        <div className="rp__chips">
          {locations.map((l) => (
            <button key={l.key} type="button" className={`rp__chip${avoid.includes(l.key) ? ' is-off' : ''}`}
                    onClick={() => toggle(l.key, avoid, setAvoid, preferred, setPreferred)}>
              {l.short ?? l.key.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="rp__form-row rp__form-row--inline">
        <label className="rp__check">
          <input type="checkbox" checked={noWeekends} onChange={(e) => setNoWeekends(e.target.checked)} />
          {t('Mai weekend e festivi')}
        </label>
        <label className="rp__field">
          <span className="muted">{t('Max giorni/sett.')}</span>
          <input className="input rp__num" type="number" min={1} max={7} value={maxDays} placeholder="—"
                 onChange={(e) => setMaxDays(e.target.value)} />
        </label>
        <button className="btn btn--brand btn--sm" disabled={saving} onClick={save}>{saving ? t('Salvataggio…') : t('Salva')}</button>
      </div>
    </div>
  );
}
